import { createTheme } from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';

const LIVID_BROWN_COLOR = '#542D32';
const WHITE_LINEN_COLOR = '#FAF5EE';

const outlinedNotchedOutlineStyles = {
  borderColor: `${LIVID_BROWN_COLOR} !important`,
  borderWidth: '1px !important',
  borderRadius: '0 !important',
  padding: '0',
};

type OutlinedRootOptions = {
  withMargin?: boolean;
};

const createOutlinedInputRootStyles = (notchedClass: string, options: OutlinedRootOptions = {}) => {
  const selector = `.${notchedClass}`;

  const styles: Record<string, unknown> = {
    borderRadius: '0 !important',
    boxShadow: 'none !important',
    backgroundColor: 'transparent',
    [`&:hover ${selector}`]: outlinedNotchedOutlineStyles,
    [`&.Mui-focused ${selector}`]: outlinedNotchedOutlineStyles,
    [`&.Mui-focused:hover ${selector}`]: outlinedNotchedOutlineStyles,
    '&.Mui-focused': {
      boxShadow: 'none !important',
    },
    [`&.Mui-error ${selector}`]: outlinedNotchedOutlineStyles,
  };

  if (options.withMargin) {
    styles.mb = { xs: 1.5, md: 2 };
  }

  return styles;
};

const outlinedInputFieldStyles = {
  borderRadius: '0 !important',
  padding: '8px 12px !important',
};

const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    // Brand mapping to core palette slots
    primary: {
      main: LIVID_BROWN_COLOR, // brand brown
      contrastText: WHITE_LINEN_COLOR, // brand beige
    },
    secondary: {
      main: WHITE_LINEN_COLOR, // brand beige
      contrastText: LIVID_BROWN_COLOR,
    },
    background: {
      default: WHITE_LINEN_COLOR,
      paper: '#FFFFFF',
    },
    text: {
      primary: LIVID_BROWN_COLOR,
      secondary: '#6d474b',
    },
    // Custom brand colors for easy access: theme.palette.brand.*
    brand: {
      brown: { main: LIVID_BROWN_COLOR, contrastText: WHITE_LINEN_COLOR },
      beige: { main: WHITE_LINEN_COLOR, contrastText: LIVID_BROWN_COLOR },
      dark: { main: '#472426', contrastText: '#FFFFFF' },
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: 'var(--font-roboto)',
    pageContent: {
      fontFamily: 'BigDailyShort',
      fontWeight: 300,
      fontSize: '1.25rem',
      // lineHeight: '100%',
      letterSpacing: '0%',
    },
    sectionTitle: {
      fontFamily: 'Centra',
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: '100%',
      letterSpacing: '2rem',
      // color: 'text.primary',
      textAlign: 'center',
      marginTop: '0',
      marginBottom: '3rem',
      color: 'red',
    },
    pageListItemTitle: {
      fontFamily: 'Centra',
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: '29px',
      letterSpacing: '1px',
      color: 'text.primary',
      textTransform: 'uppercase',
      marginTop: '0',
    },
    pageListItemSecondary: {
      fontFamily: 'Centra',
      fontWeight: 400,
      fontSize: '1.25rem',
      lineHeight: '20px',
      color: 'text.primary',
      textTransform: 'uppercase',
      marginTop: '0',
    },
    accordionRowTitle: {
      fontFamily: 'Centra',
      fontSize: '1.25rem',
      fontWeight: 300,
      letterSpacing: '1.7px',
      textTransform: 'uppercase',
    },
    billingBody: {
      fontFamily: 'BigDailyShort',
      fontWeight: 300,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    listHeaderTitle: {
      fontFamily: 'Centra',
      fontSize: '1.25rem',
      fontWeight: 400,
      textTransform: 'uppercase',
      letterSpacing: 0,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        ':root, body, #__next': {
          backgroundColor: themeParam.palette.background.default,
          color: themeParam.palette.text.primary,
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        img: {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
        },
        a: {
          color: 'inherit',
        },
      }),
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '0',
          textTransform: 'uppercase',
          borderWidth: '2px',
          // fontFamily: 'FortAd',
          fontSize: '1.06rem',

          height: '35px',
          padding: '0 32px',
          fontWeight: 400,
        },
      },
      variants: [
        {
          props: { variant: 'contained' },
          style: {
            borderRadius: 0,
            width: '100%',
            border: `2px solid ${LIVID_BROWN_COLOR}`,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: LIVID_BROWN_COLOR,

            //fonts
            fontFamily: 'Centra',
            fontWeight: 400,
            fontStyle: 'light',
            fontSize: '1.25rem',
            textTransform: 'uppercase',

            '&:hover': {
              backgroundColor: `${WHITE_LINEN_COLOR} !important`,
              color: LIVID_BROWN_COLOR,
              // mixBlendMode: 'difference',
            },
          },
        },
        {
          props: { variant: 'outlinedDark' },
          style: {
            borderRadius: 0,
            width: '100%',
            padding: '0 25px',
            background: 'transparent',
            border: '2px solid rgba(232, 230, 223, 1)',
            display: 'flex',
            alignItems: 'center',

            //font
            fontFamily: 'Centra',
            fontWeight: 300,
            fontStyle: 'light',
            fontSize: '1.25rem',
            textTransform: 'uppercase',
            color: 'rgba(232, 230, 223, 1)',

            '&:hover': {
              background: 'rgba(232, 230, 223, 1)',
              color: 'black',
              // mixBlendMode: 'difference',
            },
          },
        },
        {
          props: { variant: 'outlinedLight' },
          style: {
            borderRadius: 0,
            width: '100%',
            padding: '0 25px',
            background: 'transparent',
            border: `2px solid ${LIVID_BROWN_COLOR}`,
            display: 'flex',
            alignItems: 'center',

            //font
            fontFamily: 'Centra',
            fontWeight: 400,
            fontStyle: 'light',
            fontSize: '1.25rem',
            textTransform: 'uppercase',
            color: LIVID_BROWN_COLOR,
            transition: 'color 0.2s ease, background-color 0.2s ease',
            '& .MuiButton-startIcon': {
              color: LIVID_BROWN_COLOR,
              transition: 'color 0.2s ease, filter 0.2s ease',
              '& svg': {
                fill: 'currentColor',
              },
              '& img': {
                filter: 'none',
                transition: 'filter 0.2s ease',
              },
            },

            '&:hover': {
              background: 'unset',
              color: 'white',
              // mixBlendMode: 'difference',
              backgroundColor: LIVID_BROWN_COLOR,
            },
            '&:hover .MuiButton-startIcon': {
              color: WHITE_LINEN_COLOR,
              '& img': {
                filter: 'brightness(0) invert(1)',
              },
            },
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgb(250, 245, 238)',
          boxShadow: 'none',
          // padding: '0 40px',
        },
        // root: {
        //   '& .MuiDialogContent-root': {
        //     padding: '0',
        //   },
        // },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          '@media (min-width:900px)': { minHeight: 90 },
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'sectionTitle' },
          style: ({ theme }) => ({
            textTransform: 'uppercase',
            letterSpacing: '0em',
            color: theme.palette.text.primary,
          }),
        },
        {
          props: { variant: 'srOnly' },
          style: {
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: createOutlinedInputRootStyles('MuiOutlinedInput-notchedOutline', { withMargin: true }),
        notchedOutline: outlinedNotchedOutlineStyles,
        input: outlinedInputFieldStyles,
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          ...createOutlinedInputRootStyles('MuiPickersOutlinedInput-notchedOutline'),

          '& > .MuiPickersSectionList-root': {
            padding: '8px 0',
          },
        },
        notchedOutline: outlinedNotchedOutlineStyles,
        input: outlinedInputFieldStyles,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.MuiFormLabel-root': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          height: '23px',
          width: '23px',

          '&.MuiCheckbox-root': {
            svg: {
              padding: '0 !important',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { severity: 'info' },
              style: {
                borderColor: 'rgb(71, 36, 38)',
                backgroundColor: 'rgb(250, 245, 238)',
                color: 'rgb(71, 36, 38)',
                borderRadius: '8px',
                marginTop: '16px',
                marginBottom: '16px',
                '& .MuiAlert-icon': {
                  display: 'none',
                },
              },
            },
            {
              props: { severity: 'error' },
              style: {
                borderColor: 'rgb(71, 36, 38)',
                backgroundColor: 'rgb(253, 230, 234)',
                color: 'rgb(71, 36, 38)',
                borderRadius: '8px',
                marginTop: '16px',
                marginBottom: '16px',
                '& .MuiAlert-icon': {
                  display: 'none',
                },
              },
            },
          ],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 'unset',
          maxHeight: '42px',
          '& .MuiTabs-list': {
            justifyContent: 'center',
            maxHeight: '42px',
          },
          '& .MuiTab-root': {
            borderRight: `1.5px solid ${LIVID_BROWN_COLOR}`,

            fontFamily: 'Centra',
            fontSize: '28px',
            fontWeight: 300,

            padding: '0px 40px',

            maxHeight: '42px',
            minHeight: 'unset',
            height: '42px',
            boxSizing: 'border-box',
            transition: 'all 0.1s ease-in-out',

            '&:first-child': {
              padding: '0px 40px 0px 10px',
            },

            '&:last-child': {
              borderRight: 'unset',
              padding: '0px 10px 0px 40px',
            },

            '&.Mui-selected': {
              fontWeight: 400,
              textShadow: '0px 4px 4px rgba(0, 0, 0, 0.25);',
            },

            '& span.MuiTouchRipple-root': {
              maxHeight: '42px',
              display: 'none',
            },
          },
          '& .MuiTabs-indicator': { display: 'none' },
        },
      },
    },
  },
});

export default theme;
